// bench-E-095.CB-F.hop-secure-standup: hop-pwa now sits behind the pwa-auth-gateway.
// The JWT lives ONLY in an httpOnly `ops_session` cookie the gateway sets — client JS
// can never read it (no IndexedDB/localStorage/sessionStorage/global holding a raw
// SESSION token). Identity + roles come from GET /auth/session, cached in this module's
// closure for the life of the tab. ML remains the sole signature-verifier + authz
// enforcer; everything here is UI-only convenience, never a security boundary.
//
// SW-BACKGROUND-DRAIN: the CSRF NONCE is persisted to IndexedDB, and that does NOT weaken
// the rule above. It is not a credential: it is a double-submit nonce the gateway issues in
// a deliberately NON-httpOnly cookie so page JS can echo it back. It is already readable by
// any script on this origin; storing it adds no exposure. The session token remains httpOnly
// and untouched.
//
// WHY IT MUST BE PERSISTED AT ALL: the service worker runs its own drain on background sync.
// It has no `document`, so it cannot read the cookie, and background sync fires when the page
// is CLOSED, so there is no client to postMessage. Durable storage is the ONLY channel that
// reaches it. Without this, every background write is rejected 403 csrf_validation_failed.

export interface SessionUser {
  id: string
  username: string
  roles: string[]
}

interface SessionState {
  authenticated: boolean
  user?: SessionUser
  // R413 (c): the gateway's /auth/session carries this flag when the owner set this
  // person's password; they must choose their own before anything else. False unless the
  // gateway sent exactly `true`.
  mustChangePassword: boolean
  /**
   * R493 (Frank #66467): the HTTP status GET /auth/session answered with; null when no
   * response came back (offline, a timeout, a network error). Only a 401 means the gateway
   * refused this cookie; every other failure is "could not check", not "signed out".
   */
  checkStatus: number | null
}

let _session: SessionState | null = null
let _sessionPromise: Promise<SessionState> | null = null

/** Must match sw.js: AUTH_DB_NAME / store 'auth' / key CSRF_KEY. */
const AUTH_DB_NAME = 'tkf-hop-auth'
const CSRF_KEY = 'csrf'
/**
 * Must match sw.js SUB_KEY. R413 (c): the signed-in user's id, so the worker sends only that
 * person's queued writes. Like the nonce it is not a credential; it is written whenever a
 * session fetch comes back with a user, and cleared on sign-out or an unauthenticated fetch.
 */
const SUB_KEY = 'sub'

function openAuthDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(AUTH_DB_NAME, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('auth')) db.createObjectStore('auth')
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/**
 * Hand the current CSRF nonce to the service worker.
 *
 * Best-effort by design: a failure here must never break the page, because the FOREGROUND
 * write path does not depend on it (it reads the cookie directly). Only the background
 * drain does, and that path now reports HOP_CSRF_MISSING rather than failing silently.
 */
export async function persistCsrfForWorker(token?: string): Promise<void> {
  const value = token ?? getCSRFToken()
  if (!value) return
  try {
    const db = await openAuthDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('auth', 'readwrite')
      tx.objectStore('auth').put(value, CSRF_KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch {
    /* non-fatal — see above */
  }
}

/**
 * R413 (c): hand the signed-in user's id to the worker (null clears it). Best-effort like the
 * nonce: a failure here never breaks the page. A missing id fails SAFE: the worker then
 * sends nothing.
 */
let _subWrite: Promise<void> = Promise.resolve()

function persistSubForWorker(sub: string | null): Promise<void> {
  // Writes run in call order, so a 401's clear can never land after a later sign-in's write.
  _subWrite = _subWrite.then(() => writeSub(sub))
  return _subWrite
}

/**
 * R413 (c), Frank #62950: clear the worker's stored owner and wait for it. Called BEFORE a
 * sign-in posts /auth/login: from that POST on the browser may hold a different person's
 * cookie, and until refreshSession writes the new owner the worker must send nothing.
 * Rides the same in-order chain as every other owner write.
 */
export function clearWorkerOwner(): Promise<void> {
  return persistSubForWorker(null)
}

/** Settles once every stored-owner write issued so far has landed - test use only. */
export function _subWriteForTest(): Promise<void> {
  return _subWrite
}

async function writeSub(sub: string | null): Promise<void> {
  try {
    const db = await openAuthDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('auth', 'readwrite')
      const store = tx.objectStore('auth')
      if (sub) store.put(sub, SUB_KEY)
      else store.delete(SUB_KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch {
    /* non-fatal - see above */
  }
}

/** The cached session user's id, or null when there is no signed-in user. */
export function currentSub(): string | null {
  return _session?.user?.id ?? null
}

async function fetchSession(): Promise<SessionState> {
  try {
    const res = await fetch('/auth/session', { headers: { Accept: 'application/json' } })
    const checkStatus = typeof res.status === 'number' ? res.status : null
    if (!res.ok) return { authenticated: false, mustChangePassword: false, checkStatus }
    const data = (await res.json()) as { authenticated?: boolean; user?: SessionUser; mustChangePassword?: unknown }
    return data.authenticated && data.user
      ? { authenticated: true, user: data.user, mustChangePassword: data.mustChangePassword === true, checkStatus }
      : { authenticated: false, mustChangePassword: false, checkStatus }
  } catch {
    return { authenticated: false, mustChangePassword: false, checkStatus: null }
  }
}

/** R493: true only when the last session check got a 401 from the gateway. */
export function sessionRefused(session: SessionState): boolean {
  return session.checkStatus === 401
}

/**
 * Two-tab fix (Frank #63294): the user id the browser's CURRENT cookie names, read from
 * GET /auth/session. Another tab may have signed someone else in since this tab last
 * read its session. Only a 200 with authenticated: true and a non-empty user.id counts;
 * anything else (network error, non-ok, bad body) is null. Read-only: it never adopts
 * the session, never writes or clears the stored owner or csrf, and never touches the
 * queue's pause state.
 */
export async function readLiveSessionUserId(): Promise<string | null> {
  try {
    const res = await fetch('/auth/session', { headers: { Accept: 'application/json' } })
    if (!res.ok) return null
    const data = (await res.json()) as { authenticated?: unknown; user?: { id?: unknown } } | null
    if (!data || data.authenticated !== true || !data.user) return null
    const id = data.user.id
    return typeof id === 'string' && id !== '' ? id : null
  } catch {
    return null
  }
}

/** True when the cached session says this person must choose their own password; false when unknown. */
export function mustChangePassword(): boolean {
  return _session?.mustChangePassword === true
}

/** Bootstrap call — fetches (and memoizes) the session once per tab. */
export async function initializeSession(): Promise<SessionState> {
  if (_session) return _session
  if (!_sessionPromise) _sessionPromise = fetchSession()
  _session = await _sessionPromise
  if (_session.authenticated) void persistCsrfForWorker()
  await persistSubForWorker(currentSub())
  return _session
}

/** Force a re-fetch — used after login/re-auth to replace a stale/absent cache entry. */
export async function refreshSession(): Promise<SessionState> {
  _sessionPromise = fetchSession()
  _session = await _sessionPromise
  // ROTATION: re-auth can mint a NEW nonce. Re-persisting here is what stops a stale copy
  // from silently 403-ing every background write — the original defect in a fresh coat.
  if (_session.authenticated) void persistCsrfForWorker()
  // R413 (c): the worker's owner follows the session: a new person, a new owner; none, none.
  await persistSubForWorker(currentSub())
  return _session
}

export const COCKPIT_ROLES = ['hop', 'admin', 'owner', 'architect'] as const
// M3 role model (reconciles #93 RBAC drift): write-capability = admin/owner/hop.
// Phantom `operator` is gone — `hop` is the real operator role. `architect` is the
// one COCKPIT_ROLES member that is read-only unless it also holds a write role.
export const WRITE_ROLES = ['hop', 'admin', 'owner'] as const

export function decodeRoles(): string[] {
  return _session?.user?.roles ?? []
}

export async function hasCockpitAccess(): Promise<boolean> {
  await initializeSession()
  return decodeRoles().some((r) => (COCKPIT_ROLES as readonly string[]).includes(r))
}

export function isReadOnly(): boolean {
  const roles = decodeRoles()
  return roles.includes('architect') && !roles.some((r) => (WRITE_ROLES as readonly string[]).includes(r))
}

// bench-STAFF-PASSWORD-RESET-SCREEN.CB-B §1.6: owner or architect — including a pure
// architect (isReadOnly() === true elsewhere). The reset screen is NOT gated by
// write-capability; it does not consult isReadOnly.
export function canManageStaff(): boolean {
  const roles = decodeRoles()
  return roles.includes('owner') || roles.includes('architect')
}

const XSRF_COOKIE_NAME = 'XSRF-TOKEN'
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/** Reads the gateway's non-httpOnly double-submit CSRF cookie. */
export function getCSRFToken(): string {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${XSRF_COOKIE_NAME}=([^;]+)`))
  return match ? decodeURIComponent(match[1]) : ''
}

/**
 * fetch() wrapper that echoes the CSRF cookie back as a header on state-changing calls, and
 * — bench-STAFF-PASSWORD-RESET-SCREEN.CB-B §1.10 — pauses the offline queue and fires
 * hop:auth-required on a 401, so a reset-ended session lands on the sign-in form on its NEXT
 * read, not just its next queued write. Still returns the response either way.
 */
export async function secureFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase()
  const headers = new Headers(options.headers)
  if (WRITE_METHODS.has(method)) {
    headers.set('X-XSRF-TOKEN', getCSRFToken())
  }
  const res = await fetch(url, { ...options, headers })
  if (res.status === 401) {
    pauseQueue()
  }
  if (res.status === 403 && (await isPasswordChangeRequired(res))) {
    window.dispatchEvent(new CustomEvent('hop:password-change-required'))
  }
  return res
}

/**
 * R413 (c): a 403 is either the master layer's PASSWORD_CHANGE_REQUIRED (body
 * { error: { code } }) or something else, e.g. the gateway's CSRF refusal (body
 * { error: 'csrf_validation_failed' }, a string). Reads a CLONE so the caller can still read
 * the body; a non-JSON or unreadable body is simply "not the must-change case".
 */
async function isPasswordChangeRequired(res: Response): Promise<boolean> {
  try {
    if (typeof res.clone !== 'function') return false
    const body = (await res.clone().json()) as { error?: { code?: unknown } | string } | null
    const error = body?.error
    return typeof error === 'object' && error !== null && error.code === 'PASSWORD_CHANGE_REQUIRED'
  } catch {
    return false
  }
}

/** The signed-in user from the cached session, or null when there is none. */
export function currentUser(): { username: string; roles: string[] } | null {
  const user = _session?.user
  return user ? { username: user.username, roles: user.roles } : null
}

/** Ends the session through the gateway; true when the logout was accepted (2xx). */
export async function signOut(): Promise<boolean> {
  try {
    const res = await secureFetch('/auth/logout', { method: 'POST' })
    if (!res.ok) return false
    // R413 (c): no owner for the worker once signed out (refreshSession clears it too).
    await persistSubForWorker(null)
    await refreshSession()
    return true
  } catch {
    return false
  }
}

let _queuePaused = false
// R413 (c), Rook #62940: whose session a 401 paused the queue under. Unowned (pre-sub) items
// drain only on a re-auth by that same person; see pausedSub().
let _pausedSub: string | null = null

export function isQueuePaused(): boolean {
  return _queuePaused
}

/** The user id that was signed in when a 401 paused the queue; null when none. */
export function pausedSub(): string | null {
  return _pausedSub
}

export function pauseQueue(): void {
  _queuePaused = true
  _pausedSub = currentSub() ?? _pausedSub
  // R413 (c), Frank #62941: a 401 ends this session, so the worker loses its owner until the
  // next successful sign-in writes the new one. (A must-change pause keeps it: same person.)
  void persistSubForWorker(null)
  window.dispatchEvent(new CustomEvent('hop:auth-required'))
}

/**
 * R413 (c): pause the queue like a 401 does, but for a 403 PASSWORD_CHANGE_REQUIRED: the
 * session is alive, the person must choose their own password first, so the event is
 * hop:password-change-required, not the re-auth overlay's hop:auth-required.
 */
export function pauseQueueForPasswordChange(): void {
  _queuePaused = true
  window.dispatchEvent(new CustomEvent('hop:password-change-required'))
}

export function resumeQueue(): void {
  _queuePaused = false
  _pausedSub = null
}

/** Reset module state — test use only. */
export function _resetForTest(): void {
  _session = null
  _sessionPromise = null
  _queuePaused = false
  _pausedSub = null
}
